import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  CircularProgress,
  Chip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CategoryIcon from '@mui/icons-material/Category';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch, FaTrash } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import ConfirmationModal from '../../common/ConfirmationModal';
import {
  MainContainer,
  SegmentsGrid,
  SegmentCard,
  SegmentCardContent,
  SegmentTitle,
  EmployeeBadge,
  ActionButtons,
  UsageBadgesContainer,
  UsageBadge,
  CreatedAtText
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { radarApi } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import { getRelativeTime } from '../../../utils/dateUtils';
import SkillsItems from '../../MyActivities/MyCV/components/common/SkillsItems';

const TechnologyReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customSegments, setCustomSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentSegment, setCurrentSegment] = useState({ id: null, name: '' });
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    segment: null,
    deleting: false
  });

  // Load custom segments on component mount
  useEffect(() => {
    fetchCustomSegments();
  }, []);

  const fetchCustomSegments = async () => {
    try {
      setLoading(true);
      const response = await radarApi.getCustomSegments();
      setCustomSegments(response || []);
    } catch (error) {
      console.error('Error fetching custom segments:', error);
      showAlert('Failed to load custom segments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (segment = null) => {
    if (segment) {
      setCurrentSegment({
        id: segment.id,
        name: segment.name
      });
      setSelectedSkills([]);
    } else {
      setCurrentSegment({ id: null, name: '' });
      setSelectedSkills([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSkills([]);
    setExpandedIndex(null);
  };

  const handleSkillsChange = (newSkills) => {
    setSelectedSkills(newSkills);
  };

  const handleReconcileSegment = async () => {
    if (selectedSkills.length === 0) {
      showAlert('Please select a technology segment to reconcile with', 'error');
      return;
    }

    try {
      setReconciling(true);
      const result = await radarApi.reconcileCustomSegment(
        currentSegment.id,
        selectedSkills[0].id
      );

      showAlert(
        `Successfully reconciled "${result.data.customSegmentName}" with "${result.data.leafSegmentName}". ` +
        `Updated ${result.data.employeeSegmentsUpdated} employee(s) and ${result.data.projectSegmentsUpdated} project(s).`,
        'success'
      );

      // Refresh segments list
      await fetchCustomSegments();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling segment:', error);
      showAlert(`Failed to reconcile segment: ${error.message}`, 'error');
    } finally {
      setReconciling(false);
    }
  };

  const handleDeleteClick = (segment) => {
    setDeleteModal({
      open: true,
      segment,
      deleting: false
    });
  };

  const handleDelete = async () => {
    if (!deleteModal.segment) return;

    try {
      setDeleteModal(prev => ({ ...prev, deleting: true }));

      const result = await radarApi.deleteCustomSegment(deleteModal.segment.id);

      showAlert(
        `Successfully deleted "${result.data.customSegmentName}". ` +
        `Removed ${result.data.employeeSegmentsDeleted} employee segment(s) and ${result.data.projectSegmentsDeleted} project segment(s).`,
        'success'
      );

      // Refresh segments list
      await fetchCustomSegments();
    } catch (error) {
      console.error('Error deleting custom segment:', error);
      showAlert(error.message || 'Failed to delete custom segment', 'error');
    } finally {
      // Close the modal
      setDeleteModal({ open: false, segment: null, deleting: false });
    }
  };

  // Filter segments based on search term
  const filteredSegments = customSegments.filter(segment =>
    segment.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    (segment.segment && segment.segment.name.toLowerCase().includes((searchTerm || '').toLowerCase())) ||
    segment.employees?.some(emp => emp.fullName.toLowerCase().includes((searchTerm || '').toLowerCase())) ||
    segment.projects?.some(proj => proj.name.toLowerCase().includes((searchTerm || '').toLowerCase()))
  );

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(-1)}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Technology Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Custom Segments"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredSegments.length}
              </span>
              custom {filteredSegments.length === 1 ? 'segment' : 'segments'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading custom segments..." />
          ) : filteredSegments.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px dashed #dee2e6'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6c757d',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                No custom segments found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#adb5bd',
                  maxWidth: '400px'
                }}
              >
                {searchTerm ?
                  "No custom segments match your search criteria. Try adjusting your search term." :
                  "No custom segments have been created yet. Custom segments are created when individuals or projects add technologies that don't exist in the radar."}
              </Typography>
            </Box>
          ) : (
            <SegmentsGrid>
              {filteredSegments.map((segment) => (
                <SegmentCard key={segment.id}>
                  <SegmentCardContent>
                    <SegmentTitle>
                      {segment.name}
                      <UsageBadgesContainer>
                        {segment.employees?.length > 0 && (
                          <UsageBadge>
                            <PersonIcon /> {segment.employees.length} {segment.employees.length === 1 ? 'Individual' : 'Individuals'}
                          </UsageBadge>
                        )}
                        {segment.projects?.length > 0 && (
                          <UsageBadge>
                            <WorkIcon /> {segment.projects.length} {segment.projects.length === 1 ? 'Project' : 'Projects'}
                          </UsageBadge>
                        )}
                      </UsageBadgesContainer>
                    </SegmentTitle>

                    {segment.segment && (
                      <EmployeeBadge>
                        <CategoryIcon fontSize="small" /> Part of <span style={{ fontWeight: 600, color: COLORS.PRIMARY, marginLeft: '4px' }}>{segment.segment.name}</span>
                      </EmployeeBadge>
                    )}

                    {segment.employees?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {segment.employees.slice(0, 3).map((employee) => (
                          <Chip
                            key={employee.id}
                            label={employee.fullName}
                            size="small"
                            icon={<PersonIcon />}
                            sx={{
                              fontSize: '11px',
                              height: '24px',
                              '& .MuiChip-icon': { fontSize: '14px' }
                            }}
                          />
                        ))}
                        {segment.employees.length > 3 && (
                          <Chip
                            label={`+${segment.employees.length - 3} more`}
                            size="small"
                            sx={{
                              fontSize: '11px',
                              height: '24px',
                              backgroundColor: '#e2e8f0'
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {segment.projects?.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {segment.projects.slice(0, 3).map((project) => (
                          <Chip
                            key={project.id}
                            label={project.code ? `${project.name} (${project.code})` : project.name}
                            size="small"
                            icon={<WorkIcon />}
                            variant="outlined"
                            sx={{
                              fontSize: '11px',
                              height: '24px',
                              '& .MuiChip-icon': { fontSize: '14px' }
                            }}
                          />
                        ))}
                        {segment.projects.length > 3 && (
                          <Chip
                            label={`+${segment.projects.length - 3} more`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '11px',
                              height: '24px'
                            }}
                          />
                        )}
                      </Box>
                    )}

                    {segment.createdAt && (
                      <CreatedAtText>
                        <AccessTimeIcon /> Added {getRelativeTime(segment.createdAt)}
                        {segment.createdBy && ` by ${segment.createdBy}`}
                      </CreatedAtText>
                    )}

                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(segment)}
                        title="Reconcile with existing technology segment"
                        color="primary"
                      >
                        <LinkIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(segment)}
                        title="Delete custom segment"
                        color="error"
                      >
                        <FaTrash />
                      </IconButton>
                    </ActionButtons>
                  </SegmentCardContent>
                </SegmentCard>
              ))}
            </SegmentsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Segment Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            maxWidth: { xs: '95%', sm: '80%', md: '800px' },
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle>Reconcile Custom Segment</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Reconcile the custom segment <strong>"{currentSegment.name}"</strong> with an existing technology in the radar.
            This will update all employee and project associations to use the selected technology, then delete the custom segment.
          </DialogContentText>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1rem', fontWeight: 500 }}>
              Select a Technology to Reconcile With
            </Typography>
            <SkillsItems
              skills={selectedSkills}
              onSkillsChange={handleSkillsChange}
              expandedIndex={expandedIndex}
              setExpandedIndex={setExpandedIndex}
              globalSearch={true}
              hideLevels={true}
              addButtonText="Add a technology from radar"
              emptyStateTitle="No technology selected"
              emptyStateDescription="Select a technology from the radar to reconcile with the custom segment."
              singleSelection={true}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={reconciling}>
            Cancel
          </Button>
          <Button
            onClick={handleReconcileSegment}
            variant="contained"
            startIcon={reconciling ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
            disabled={!selectedSkills[0]?.id || reconciling}
            sx={{
              bgcolor: COLORS.PRIMARY,
              '&:hover': {
                bgcolor: COLORS.PRIMARY_DARK,
              }
            }}
          >
            {reconciling ? 'Reconciling...' : 'Reconcile'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, segment: null, deleting: false })}
        onConfirm={handleDelete}
        title="Delete Custom Segment"
        message={
          deleteModal.segment
            ? `Are you sure you want to delete the custom segment "${deleteModal.segment.name}"? This action cannot be undone and will remove all employee and project associations with this segment.`
            : ""
        }
        loading={deleteModal.deleting}
      />
    </Container>
  );
};

export default TechnologyReconciliation;
